import { useState, useCallback, useRef, useEffect } from 'react';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { useAuthUser } from '@/hooks/useAuthUser';
import { UIImageCategory, CreateUIImageParams } from '@/modules/uiImages/types';
import { StandardCropData } from '@/modules/uiImages/cropUtils';

// Helper function to convert data URL to blob
const dataURLToBlob = (dataURL: string): Blob => {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

export interface ImageUploadOptions {
  maxSizeInMB?: number;
  enableCrop?: boolean;
  validateAspectRatio?: boolean;
  autoUpload?: boolean;
}

export interface ImageUploadResult {
  success: boolean;
  imageId?: string;
  originalUrl?: string;
  croppedUrl?: string;
  crop?: StandardCropData;
  error?: string;
}

export interface ImageUploadState {
  isUploading: boolean;
  isCropping: boolean;
  tempImage: string;
  tempFile: File | null;
  error: string | null;
}

export const useImageUpload = (
  imageCategory: UIImageCategory,
  contextId?: string,
  options: ImageUploadOptions = {}
) => {
  const {
    maxSizeInMB = 20,
    enableCrop = true,
    autoUpload = false
  } = options;

  const services = useServices();
  const { user } = useAuthUser();
  const userId = user?.id;
  
  const [state, setState] = useState<ImageUploadState>({
    isUploading: false,
    isCropping: false,
    tempImage: '',
    tempFile: null,
    error: null
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cleanup temp image URLs
  useEffect(() => {
    return () => {
      if (state.tempImage && state.tempImage.startsWith('blob:')) {
        try { 
          URL.revokeObjectURL(state.tempImage); 
        } catch {
          // Already released: nothing left to free
        }
      }
    };
  }, [state.tempImage]);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const setUploading = useCallback((isUploading: boolean) => {
    setState(prev => ({ ...prev, isUploading }));
  }, []);

  const setCropping = useCallback((isCropping: boolean) => {
    setState(prev => ({ ...prev, isCropping }));
  }, []);

  const setTempImage = useCallback((tempImage: string) => {
    setState(prev => ({ ...prev, tempImage }));
  }, []);

  const setTempFile = useCallback((tempFile: File | null) => {
    setState(prev => ({ ...prev, tempFile }));
  }, []);

  const validateFile = useCallback((file: File): { isValid: boolean; error?: string } => {
    // Check file size
    if (file.size > maxSizeInMB * 1024 * 1024) {
      return {
        isValid: false,
        error: `File size must be less than ${maxSizeInMB}MB`
      };
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'File must be a JPEG, PNG, or WebP image'
      };
    }

    return { isValid: true };
  }, [maxSizeInMB]);

  const uploadFile = useCallback(async (
    file: File,
    croppedImageUrl?: string,
    cropData?: StandardCropData
  ): Promise<ImageUploadResult> => {
    if (!userId) {
      return {
        success: false,
        error: 'User not authenticated'
      };
    }

    try {
      setUploading(true);
      setError(null);

      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop() || 'jpg';
      
      // Upload original image
      const originalFilename = `${imageCategory}-original-${timestamp}_${Math.random().toString(36).substring(2)}.${fileExtension}`;
      const originalFilePath = `ui-images/${contextId || userId}/${originalFilename}`;

      const { data: originalStored, error: originalUploadError } = await services.storage.uploadFile(
        originalFilePath,
        file,
        {
          contentType: file.type,
          cacheControl: '3600',
          upsert: false
        }
      );

      if (originalUploadError) {
        throw new Error(`Original upload failed: ${originalUploadError.message}`);
      }

      // The address the upload answered: the stored name has a version in it, so nothing is cached under it before
      if (!originalStored?.url) {
        throw new Error('Failed to get public URL for original image');
      }
      const originalUrl = originalStored.url;

      let croppedUrl: string | undefined;
      let finalCropData: StandardCropData | undefined;

      // Upload cropped image if provided
      if (croppedImageUrl && enableCrop) {
        const croppedBlob = dataURLToBlob(croppedImageUrl);
        const croppedFilename = `${imageCategory}-cropped-${timestamp}_${Math.random().toString(36).substring(2)}.png`;
        const croppedFilePath = `ui-images/${contextId || userId}/${croppedFilename}`;

        const { data: croppedStored, error: croppedUploadError } = await services.storage.uploadFile(
          croppedFilePath,
          croppedBlob,
          {
            contentType: 'image/png',
            cacheControl: '3600',
            upsert: false
          }
        );

        if (croppedUploadError) {
          throw new Error(`Cropped upload failed: ${croppedUploadError.message}`);
        }

        if (!croppedStored?.url) {
          throw new Error('Failed to get public URL for cropped image');
        }
        croppedUrl = croppedStored.url;
        finalCropData = cropData;
      }

      // Only create database record if autoUpload is enabled
      // Otherwise, let the parent component handle database creation
      if (autoUpload) {
        const createParams: CreateUIImageParams = {
          projectInstanceId: contextId?.includes('project') ? contextId : undefined,
          sceneOriginId: contextId?.includes('scene') ? contextId : undefined,
          imageCategory,
          imageOriginalUrl: originalUrl,
          imageCroppedUrl: croppedUrl,
          userId,
          imageMetadata: finalCropData ? { crop: finalCropData } : undefined
        };

        const result = await services.uiImages.createUIImage(createParams);
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to create image record');
        }

        return {
          success: true,
          imageId: result.data?.imageId,
          originalUrl,
          croppedUrl,
          crop: finalCropData
        };
      } else {
        // Return URLs without creating database record
        return {
          success: true,
          originalUrl,
          croppedUrl,
          crop: finalCropData
        };
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setUploading(false);
    }
  }, [userId, imageCategory, contextId, enableCrop, autoUpload, services, setUploading, setError]);

  const handleFileSelect = useCallback(async (file: File): Promise<ImageUploadResult | null> => {
    const validation = validateFile(file);
    if (!validation.isValid) {
      setError(validation.error || 'Invalid file');
      return null;
    }

    const imageUrl = URL.createObjectURL(file);
    setTempImage(imageUrl);
    setTempFile(file);

    if (enableCrop) {
      setCropping(true);
      return null; // Will be handled by crop completion
    } else if (autoUpload) {
      return await uploadFile(file);
    }

    return null; // File selected, waiting for manual upload
  }, [validateFile, enableCrop, autoUpload, uploadFile, setError, setTempImage, setTempFile, setCropping]);

  const handleCropComplete = useCallback(async (
    croppedImageUrl: string,
    cropData: StandardCropData
  ): Promise<ImageUploadResult> => {
    if (!state.tempFile) {
      return {
        success: false,
        error: 'No file to upload'
      };
    }

    const result = await uploadFile(state.tempFile, croppedImageUrl, cropData);
    
    if (result.success) {
      setCropping(false);
      setTempImage('');
      setTempFile(null);
    }

    return result;
  }, [state.tempFile, uploadFile, setCropping, setTempImage, setTempFile]);

  const handleCropCancel = useCallback(() => {
    setCropping(false);
    if (state.tempImage && state.tempImage.startsWith('blob:')) {
      try { 
        URL.revokeObjectURL(state.tempImage); 
      } catch {
        // Already released: nothing left to free
      }
    }
    setTempImage('');
    setTempFile(null);
  }, [state.tempImage, setCropping, setTempImage, setTempFile]);

  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  const reset = useCallback(() => {
    setState({
      isUploading: false,
      isCropping: false,
      tempImage: '',
      tempFile: null,
      error: null
    });
  }, []);

  return {
    // State
    ...state,
    fileInputRef,
    
    // Actions
    handleFileSelect,
    handleCropComplete,
    handleCropCancel,
    uploadFile,
    clearError,
    reset,
    setCropping,
    setTempImage,
    setTempFile,
    
    // Utilities
    validateFile
  };
};
